import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, Share2, Minus, Plus, Truck, ShieldCheck, Factory, Star, MessageCircle, Camera } from "lucide-react";
import api, { imgUrl, productAlt } from "../lib/api";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import RecentlyViewed from "../components/RecentlyViewed";
import { recordView } from "../lib/recentlyViewed";
import EnquiryModal from "../components/EnquiryModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, wishlist, toggleWishlist, setCartOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [reviewData, setReviewData] = useState({ reviews: [], average: 0, count: 0 });
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, title: "", comment: "" });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewImage, setReviewImage] = useState("");
  const [reviewUploading, setReviewUploading] = useState(false);
  const touchStartX = useRef(null);

  // swipe left/right on the main image cycles photos (mobile gallery)
  const onImgTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onImgTouchEnd = (e) => {
    if (touchStartX.current == null || !product?.images?.length) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    const n = product.images.length;
    setActiveImg((i) => (dx < 0 ? (i + 1) % n : (i - 1 + n) % n));
  };

  const whatsapp = (() => {
    const d = (settings?.whatsapp || "").replace(/\D/g, "");
    return d.length === 10 ? `91${d}` : d;
  })();

  const uploadReviewPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReviewUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/customer-image", fd);
      setReviewImage(data.url);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setReviewUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get(`/products/${slug}/related`).then((r) => setRelated(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (product?.id) {
      api.get(`/products/${product.id}/reviews`).then((r) => setReviewData(r.data)).catch(() => {});
      recordView(product);
    }
  }, [product?.id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewLoading(true);
    try {
      await api.post(`/products/${product.id}/reviews`, { ...reviewForm, image: reviewImage });
      toast.success("Thank you! Your review is live.");
      setReviewForm({ name: "", rating: 5, title: "", comment: "" });
      setReviewImage("");
      const { data } = await api.get(`/products/${product.id}/reviews`);
      setReviewData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setReviewLoading(false);
    }
  };

  if (notFound) return (
    <div className="text-center py-32" data-testid="product-not-found">
      <SEO title="Product not found" noindex />
      <h1 className="font-serif text-3xl">Product not found</h1>
      <Link to="/shop" className="small-caps text-gold border-b border-gold mt-4 inline-block">Back to Shop</Link>
    </div>
  );
  if (!product) return <div className="max-w-7xl mx-auto px-8 py-24"><div className="aspect-square bg-muted animate-pulse" /></div>;

  const price = product.sale_price || product.price;
  const wished = wishlist.some((p) => p.id === product.id);
  const inStock = product.stock > 0;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: product.name, url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  const buyNow = () => {
    addToCart(product, qty);
    navigate("/checkout");
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.images?.map(imgUrl),
    sku: product.sku,
    brand: { "@type": "Brand", name: "Humaira Crafts" },
    offers: {
      "@type": "Offer",
      url: `${window.location.origin}/product/${product.slug}`,
      priceCurrency: "INR",
      price: price,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
    ...(reviewData.count > 0 ? {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: reviewData.average,
        reviewCount: reviewData.count,
      },
    } : {}),
  };

  const specs = [
    ["SKU", product.sku], ["Material", product.material], ["Finish", product.finish],
    ["Dimensions", product.dimensions], ["Weight", product.weight],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="product-detail-page">
      <SEO title={product.seo_title || product.name} description={product.seo_description || product.description?.slice(0, 155)} schema={schema} image={imgUrl(product.images?.[0])} type="product" />

      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="relative border border-black/10 bg-white overflow-hidden select-none" onTouchStart={onImgTouchStart} onTouchEnd={onImgTouchEnd}>
            <img src={imgUrl(product.images?.[activeImg])} alt={productAlt(product)} draggable={false} className="w-full aspect-square object-cover" data-testid="product-main-image" />
            {product.images?.length > 1 && (
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2.5 py-1 rounded-full tracking-wider md:hidden" data-testid="image-counter">
                {activeImg + 1}/{product.images.length}
              </span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex flex-wrap gap-3 mt-4" data-testid="product-thumbnails">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`border overflow-hidden w-20 aspect-square transition-colors duration-300 ${i === activeImg ? "border-gold" : "border-black/10 hover:border-gold"}`}
                  data-testid={`thumbnail-${i}`}>
                  <img src={imgUrl(img)} alt={`${productAlt(product)} — view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {product.video_url && (
            <div className="mt-6 aspect-video border border-black/10" data-testid="product-video">
              <iframe src={product.video_url} title="Product video" className="w-full h-full" allowFullScreen />
            </div>
          )}
        </div>

        <div className="md:sticky md:top-32 self-start">
          <p className="small-caps text-gold">{product.category_name}</p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-semibold mt-2" data-testid="product-name">{product.name}</h1>
          {reviewData.count > 0 && (
            <div className="flex items-center gap-2 mt-3" data-testid="rating-summary">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={15} strokeWidth={1.5} className={i <= Math.round(reviewData.average) ? "fill-gold text-gold" : "text-black/20"} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">{reviewData.average} · {reviewData.count} review{reviewData.count !== 1 ? "s" : ""}</span>
            </div>
          )}

          <div className="flex items-baseline gap-3 mt-6" data-testid="product-price">
            <span className="font-serif text-3xl font-semibold">₹{price.toLocaleString("en-IN")}</span>
            {product.sale_price && <span className="text-muted-foreground line-through">₹{product.price.toLocaleString("en-IN")}</span>}
          </div>
          <p className={`text-xs mt-2 ${inStock ? "text-green-800" : "text-destructive"}`} data-testid="stock-status">
            {inStock ? `In Stock (${product.stock} available)` : "Out of Stock"}
          </p>

          <p className="text-sm text-muted-foreground leading-relaxed mt-6">{product.description}</p>

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-black/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-gold transition-colors duration-300" data-testid="qty-decrease" aria-label="Decrease"><Minus strokeWidth={1.5} size={14} /></button>
              <span className="w-10 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock || 99, qty + 1))} className="p-3 hover:text-gold transition-colors duration-300" data-testid="qty-increase" aria-label="Increase"><Plus strokeWidth={1.5} size={14} /></button>
            </div>
            <button onClick={share} className="p-3 border border-black/20 hover:border-gold hover:text-gold transition-colors duration-300" data-testid="share-button" aria-label="Share">
              <Share2 strokeWidth={1.5} size={16} />
            </button>
            <button onClick={() => { toggleWishlist(product); toast(wished ? "Removed from wishlist" : "Added to wishlist"); }}
              className="p-3 border border-black/20 hover:border-gold transition-colors duration-300" data-testid="wishlist-button" aria-label="Wishlist">
              <Heart strokeWidth={1.5} size={16} className={wished ? "fill-gold text-gold" : ""} />
            </button>
          </div>

          <div className="flex flex-col gap-3 mt-6">
            <button onClick={buyNow} disabled={!inStock}
              className="w-full bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-40"
              data-testid="buy-now-button">
              Buy Now
            </button>
            <button onClick={() => { addToCart(product, qty); toast.success("Added to cart"); setCartOpen(true); }} disabled={!inStock}
              className="w-full border border-[#111] py-4 small-caps hover:bg-[#111] hover:text-[#FAF9F6] transition-colors duration-300 disabled:opacity-40"
              data-testid="add-to-cart-button">
              Add to Cart
            </button>
            <button onClick={() => setEnquiryOpen(true)}
              className="w-full border border-gold text-gold py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300"
              data-testid="bulk-enquiry-button">
              Bulk Enquiry — Wholesale Pricing
            </button>
            {whatsapp && (
              <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi Humaira Crafts, I'm interested in "${product.name}" (${window.location.origin}/product/${product.slug}).`)}`}
                target="_blank" rel="noreferrer"
                className="w-full border border-black/20 py-4 small-caps hover:border-gold hover:text-gold transition-colors duration-300 flex items-center justify-center gap-2"
                data-testid="whatsapp-enquiry">
                <MessageCircle strokeWidth={1.5} size={15} /> Chat on WhatsApp
              </a>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 mt-8 border-t border-black/10 pt-8">
            <div className="flex flex-col gap-2"><Truck strokeWidth={1.5} size={20} className="text-gold" /><p className="text-xs text-muted-foreground">Est. delivery: {settings?.estimated_delivery || "5-7 business days"}</p></div>
            <div className="flex flex-col gap-2"><Factory strokeWidth={1.5} size={20} className="text-gold" /><p className="text-xs text-muted-foreground">Manufacturer direct from Delhi</p></div>
            <div className="flex flex-col gap-2"><ShieldCheck strokeWidth={1.5} size={20} className="text-gold" /><p className="text-xs text-muted-foreground">Quality checked since 2002</p></div>
          </div>

          <Accordion type="single" collapsible className="mt-8">
            {specs.length > 0 && (
              <AccordionItem value="specs">
                <AccordionTrigger className="small-caps" data-testid="specs-accordion">Specifications</AccordionTrigger>
                <AccordionContent>
                  <table className="w-full text-sm">
                    <tbody>
                      {specs.map(([k, v]) => (
                        <tr key={k} className="border-b border-black/5">
                          <td className="py-2 text-muted-foreground w-32">{k}</td>
                          <td className="py-2">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AccordionContent>
              </AccordionItem>
            )}
            <AccordionItem value="shipping">
              <AccordionTrigger className="small-caps" data-testid="shipping-accordion">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">
                {settings?.shipping_message || "Shipping charges will be calculated during checkout or confirmed after order."}
                {" "}See our <Link to="/policies/returns" className="text-gold underline">returns policy</Link>.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      <section className="mt-24" data-testid="reviews-section">
        <h2 className="text-3xl tracking-tight font-semibold mb-10" data-eid="pd-reviews-heading" data-etype="text">Customer Reviews</h2>
        <div className="grid md:grid-cols-[1fr_360px] gap-12">
          <div className="flex flex-col gap-6">
            {reviewData.reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground bg-white border border-black/10 p-8" data-testid="no-reviews-yet">No reviews yet. Be the first to review this piece.</p>
            ) : (
              reviewData.reviews.map((r) => (
                <div key={r.id} className="bg-white border border-black/10 p-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} size={14} strokeWidth={1.5} className={i <= r.rating ? "fill-gold text-gold" : "text-black/20"} />
                      ))}
                    </div>
                    <p className="text-sm font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                  {r.title && <p className="font-serif text-lg mt-3">{r.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.comment}</p>
                </div>
              ))
            )}
          </div>
          <div className="bg-white border border-black/10 p-8 self-start sticky top-32">
            <p className="small-caps text-gold mb-6">Write a Review</p>
            <form onSubmit={submitReview} className="flex flex-col gap-4">
              <div className="flex gap-1" data-testid="review-rating-input">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setReviewForm({ ...reviewForm, rating: i })} aria-label={`${i} star`} data-testid={`rating-star-${i}`}>
                    <Star size={22} strokeWidth={1.5} className={i <= reviewForm.rating ? "fill-gold text-gold" : "text-black/20 hover:text-gold"} />
                  </button>
                ))}
              </div>
              <input required placeholder="Your Name *" value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                className="border border-black/15 bg-transparent px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors duration-300" data-testid="review-name" />
              <input placeholder="Review Title" value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                className="border border-black/15 bg-transparent px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors duration-300" data-testid="review-title" />
              <textarea required placeholder="Your review *" rows={4} value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                className="border border-black/15 bg-transparent px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors duration-300 resize-none" data-testid="review-comment" />
              <div className="flex items-center gap-3">
                <label className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300 flex items-center gap-2 cursor-pointer" data-testid="review-photo-upload">
                  <Camera strokeWidth={1.5} size={14} /> {reviewUploading ? "Uploading..." : "Add Photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadReviewPhoto} />
                </label>
                {reviewImage && <img src={imgUrl(reviewImage)} alt="Review attachment" className="w-10 h-10 object-cover border border-black/10" data-testid="review-photo-preview" />}
              </div>
              <button type="submit" disabled={reviewLoading}
                className="bg-[#111] text-[#FAF9F6] py-3.5 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50"
                data-testid="review-submit">
                {reviewLoading ? "Publishing..." : "Submit Review"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-24" data-testid="related-products">
          <h2 className="text-3xl tracking-tight font-semibold mb-10" data-eid="pd-related-heading" data-etype="text">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      <RecentlyViewed exclude={product.id} />

      <EnquiryModal open={enquiryOpen} onClose={() => setEnquiryOpen(false)} productName={product.name} type="bulk" />
    </div>
  );
}
